import React from 'react';
import Icon from '../../../components/AppIcon';

const ParticipantTravelDetails = ({ participants, meetingTime, currentUserId }) => {
  const getTransportIcon = (method) => {
    const icons = {
      driving: 'Car',
      walking: 'PersonStanding',
      'public-transit': 'Bus',
      bike: 'Bike'
    };
    return icons?.[method] || 'MapPin';
  };

  const getTransportLabel = (method) => {
    const labels = {
      driving: 'Driving',
      walking: 'Walking',
      'public-transit': 'Public Transit',
      bike: 'Biking'
    };
    return labels?.[method] || method;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const maxTravelTime = Math.max(...participants?.map(p => p?.travelTime));
  const minTravelTime = Math.min(...participants?.map(p => p?.travelTime));
  const avgTravelTime = Math.round(participants?.reduce((sum, p) => sum + p?.travelTime, 0) / participants?.length);

  return (
    <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Icon name="Users" size={24} className="text-primary" />
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
            Participant Travel Details
          </h2>
        </div>
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-lg">
          <Icon name="Clock" size={16} />
          <span className="text-sm font-medium">Meeting at {meetingTime}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingDown" size={16} className="text-success" />
            <span className="text-sm text-muted-foreground">Shortest Travel</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{formatDuration(minTravelTime)}</p>
        </div>
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BarChart3" size={16} className="text-primary" />
            <span className="text-sm text-muted-foreground">Average Travel</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{formatDuration(avgTravelTime)}</p>
        </div>
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-warning" />
            <span className="text-sm text-muted-foreground">Longest Travel</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">{formatDuration(maxTravelTime)}</p>
        </div>
      </div>
      <div className="space-y-3">
        {participants?.map((participant) => (
          <div
            key={participant?.id}
            className="bg-background border border-border rounded-lg p-4 hover-lift transition-smooth"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  participant?.hasConfirmed ? 'bg-success/10' : 'bg-muted'
                }`}>
                  <Icon name={getTransportIcon(participant?.transportMethod)} size={20} className={participant?.hasConfirmed ? 'text-success' : 'text-primary'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-semibold text-foreground truncate">
                      {participant?.participantName}
                      {participant?.userId === currentUserId && (
                        <span className="text-xs text-muted-foreground ml-2">(You)</span>
                      )}
                    </p>
                    {participant?.hasConfirmed && (
                      <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {participant?.participantLocation}
                  </p>
                  <p className="text-sm text-primary mt-1">
                    {getTransportLabel(participant?.transportMethod)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground mb-1">Travel Time</p>
                  <p className="text-lg font-semibold text-foreground">
                    {formatDuration(participant?.travelTime)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground mb-1">Arrival Time</p>
                  <p className="text-lg font-semibold text-success">
                    {participant?.arrivalTime}
                  </p>
                </div>
              </div>
            </div>

            {participant?.hasConfirmed && participant?.confirmedAt && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Confirmed {new Date(participant.confirmedAt)?.toLocaleString()}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Travel Time Note</p>
            <p className="text-sm text-muted-foreground">
              Travel times are estimates based on current traffic conditions. Participants should plan to leave with extra time to ensure punctual arrival.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantTravelDetails;